import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Encoding, EncodingResult, MarkChannel } from '@mode-vegas/vegas';
import { ChartFacade, DragFieldInfo, SelectedChartService } from '@mode/charts/contract';
import { ChartFormatHelpers, EncodingHelpers, RenderDataModel } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChannelSummary } from '../../marks-card/marks-helpers';
import { CompiledAttr } from '@mode/shared/util-switch';
import { AccountFacade } from '@mode/accounts/contract';

@Component({
  selector: 'mode-radial-builder',
  templateUrl: './radial-builder.component.html',
  styleUrls: ['./radial-builder.component.scss'],
})
export class RadialBuilderComponent implements OnDestroy, OnChanges {
  @Input({ required: true }) encoding!: Encoding;
  @Input() observables: any;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input() isExploring = false;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;

  queryToken$: Observable<string> | undefined;
  queryRunToken$: Observable<string> | undefined;
  view$: Observable<FlamingoTypes.ModeView> | undefined;
  vizToken$: Observable<string> | undefined;
  fieldFormatMap$: Observable<ChartTypes.FieldFormatMap> | undefined;
  fieldColorMap$: Observable<ChartTypes.FieldColorMap> | undefined;
  format$: Observable<ChartTypes.ChartFormat> | undefined;
  isAutoOrManualRender$: Observable<boolean> | undefined;
  renderModel$: Observable<RenderDataModel> | undefined;
  colorPaletteToken$: Observable<string> | undefined;
  colorPaletteToken: string | undefined;

  view: FlamingoTypes.ModeView | undefined;
  dragInfo: DragFieldInfo | undefined;
  colorMarkField: ChannelSummary | undefined;
  angleMarkField: ChannelSummary | undefined;
  allColorPalettes: OrgTypes.ColorPalette[] = [];
  selectedColorPalette: OrgTypes.ColorPalette | undefined = undefined;
  defaultColorPalette: OrgTypes.ColorPalette | undefined = undefined;
  isValidForColor = false;
  useMuzeRenderer = false;

  readonly colorChannelZone = EncodingHelpers.ZoneType.Discrete;

  private destroyed$ = new Subject<void>();

  constructor(
    private chartFacade: ChartFacade,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone,
    private accountFacade: AccountFacade
  ) {
    this.chartFacade.dragField$.pipe(takeUntil(this.destroyed$)).subscribe((dragInfo: DragFieldInfo) => {
      this.dragInfo = dragInfo;
    });

    this.accountFacade.reportOwnerColorPalettes$.pipe(takeUntil(this.destroyed$)).subscribe((palettes) => {
      this.allColorPalettes = [...palettes]
        .filter((palette) => palette.type === OrgTypes.ColorPaletteType.Categorical)
        .sort((a, b) => a.name.localeCompare(b.name));
      this.defaultColorPalette = this.allColorPalettes.find((palette) => palette.isDefault);
    });

    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('dragover', this.documentDragOver);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['observables']) {
      this.queryToken$ = this.observables.queryToken$;
      this.queryRunToken$ = this.observables.queryRunToken$;
      this.view$ = this.observables.view$;
      this.view$?.pipe(takeUntil(this.destroyed$)).subscribe((view) => (this.view = view));

      this.colorPaletteToken$ = this.observables.colorPaletteToken$;
      this.colorPaletteToken$?.pipe(takeUntil(this.destroyed$)).subscribe((colorPaletteToken) => {
        this.colorPaletteToken = colorPaletteToken;
        this.selectedColorPalette = !this.colorPaletteToken
          ? this.defaultColorPalette
          : this.allColorPalettes.find((palette) => palette.token === this.colorPaletteToken);
      });

      this.vizToken$ = this.isExploring ? of(FLAMINGO_RESERVED_REFS.Exploration) : this.observables.token$;
      this.fieldFormatMap$ = this.observables.fieldFormats$;
      this.fieldColorMap$ = this.observables.fieldColors$;
      this.format$ = this.observables.format$;
      this.format$?.pipe(takeUntil(this.destroyed$)).subscribe((format) => {
        this.useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(this.chartType, format);
      });

      this.isAutoOrManualRender$ = this.observables.isAutoOrManualRender$;
      this.renderModel$ = this.observables.renderModel$;
    }
    // Don't enable the color modal button until the chart is complete because we don't have the
    // data yet to show in the modal.
    this.isValidForColor = EncodingHelpers.isChartEncodingComplete(this.encoding, this.chartType);

    const colorField = this.encoding.marks.properties.color.values[0];
    const colorMark: ChannelSummary = {
      channel: 'color',
      type: colorField != null ? 'field' : 'empty',
      field: colorField,
      id: colorField?.id,
      fieldIds: colorField?.id != null ? [colorField.id] : [],
      isValid: true,
    };
    this.colorMarkField = colorMark;

    const angleField = this.encoding.marks.properties.angle.values[0];
    const angleMark: ChannelSummary = {
      channel: 'angle',
      type: angleField != null ? 'field' : 'empty',
      field: angleField,
      id: angleField?.id,
      fieldIds: angleField?.id != null ? [angleField.id] : [],
      isValid: true,
    };
    this.angleMarkField = angleMark;
  }

  onTypeAheadSelection($event: { attr: CompiledAttr; event: any; onFinalize: () => void }, channel: MarkChannel) {
    const { attr } = $event;
    const formula = attr;

    // Bail on empty formula
    if (formula == null) {
      return;
    }

    const newEncoding = EncodingHelpers.addOrReplaceVegasField(
      EncodingHelpers.loadEncoding(this.encoding),
      formula,
      channel,
      '*',
      channel === 'color' ? this.colorChannelZone : undefined
    );
    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  onDropOnTypeAhead($event: unknown, channel: MarkChannel) {
    const dragInfo: DragFieldInfo | undefined = this.dragInfo;
    const formula = dragInfo?.formula;
    const source = dragInfo?.source;

    this.chartService.endDrag();
    // Unclear what order of events would result in dragInfo.field being null, but it seems to be possible
    if (formula == null || source == null) {
      return;
    }

    // no-op
    if (source === channel) {
      return;
    }

    let newEncoding: EncodingResult | undefined;

    if (formula.isField && formula.alias) {
      const field = channel === 'color' ? this.colorMarkField?.field : this.angleMarkField?.field;
      if (field?.id) {
        newEncoding = EncodingHelpers.replaceAndMoveVegasField(
          this.encoding,
          formula.alias,
          channel,
          field?.id ?? '',
          '*',
          formula,
          channel === 'color' ? this.colorChannelZone : undefined
        );
      }
    } else {
      newEncoding = EncodingHelpers.addOrReplaceVegasField(
        EncodingHelpers.loadEncoding(this.encoding),
        formula,
        channel,
        '*',
        channel === 'color' ? this.colorChannelZone : undefined
      );
    }

    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();

    this.document.removeEventListener('dragover', this.documentDragOver);
  }

  documentDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onPaletteAssigned(palette: OrgTypes.ColorPalette) {
    this.chartService.updateTheme('', palette.token, palette.value.split(','), true);
  }
}
